// Géneros y Subgéneros — administración de la taxonomía por tenant.
//
// Permite crear/renombrar/borrar géneros propios y sus subgéneros (relación
// padre configurable), reasignar un subgénero a otro género, y cargar la
// propuesta inicial. Alimenta los desplegables del resto de la app.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { ApiError } from '../api/client.js';
import { AlertMessage, AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import type { GenreOption, Subgenre } from '../api/endpoints.js';

export function GenreTaxonomyView() {
  const { t } = useTranslation();
  const api = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('catalog', 'read_write');
  const genres = useAsync(() => api.genres.list('propio'), []);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function createGenre(name: string) {
    await api.genres.create({ name });
  }

  async function seedDefaults() {
    if (!confirm(t('genreTax.seedConfirm')))
      return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.genres.seedDefaults();
      setMsg(t('genreTax.seedResult', { genres: r.genres_created, subgenres: r.subgenres_created }));
      genres.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('genreTax.pageTitle')}
        subtitle={t('genreTax.pageSubtitle')}
        back
        meta={
          canEdit ? (
            <button className="btn-secondary btn-sm" type="button" onClick={() => setAdding((a) => !a)}>
              <Icon name={adding ? 'close' : 'add'} className="text-[16px]" /> {adding ? t('genreTax.close') : t('genreTax.new')}
            </button>
          ) : undefined
        }
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      {canEdit && (
        <div className="flex flex-col gap-space-xs">
          <button className="btn-secondary btn-sm self-start" type="button" onClick={() => void seedDefaults()} disabled={busy}>
            <Icon name="auto_awesome" className="text-[16px]" /> {t('genreTax.seedButton')}
          </button>
          {msg && <p className="font-body-sm text-body-sm text-vacuum-glow">{msg}</p>}
        </div>
      )}

      {adding && canEdit && (
        <NameForm
          title={t('genreTax.newGenre')}
          placeholder={t('genreTax.genreNamePlaceholder')}
          onSubmit={createGenre}
          onDone={() => {
            setAdding(false);
            genres.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <AsyncBoundary state={genres}>
        {(data) => {
          if (data.items.length === 0)
            return (
              <div className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
                {t('genreTax.emptyGenres')}
              </div>
            );
          return (
            <div className="flex flex-col gap-space-sm">
              <span className="font-code-catalog text-body-sm text-on-surface-variant">
                {t('genreTax.genreCount', { count: data.items.length })}
              </span>
              {data.items.map((g) => (
                <GenreRow
                  key={g.id}
                  genre={g}
                  allGenres={data.items}
                  canEdit={canEdit}
                  onChanged={() => genres.reload()}
                />
              ))}
            </div>
          );
        }}
      </AsyncBoundary>
      </div>
    </div>
  );
}

/** Fila de un género con sus subgéneros (expandible) y acciones. */
function GenreRow({
  genre,
  allGenres,
  canEdit,
  onChanged,
}: {
  genre: GenreOption;
  allGenres: GenreOption[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subs = useAsync(() => api.genres.subgenreEntities(genre.id), [genre.id, open]);

  async function rename(name: string) {
    await api.genres.update(genre.id, { name });
  }

  async function remove() {
    setError(null);
    try {
      await api.genres.remove(genre.id);
      onChanged();
    } catch (err) {
      // Mostrar CUALQUIER error, no solo el 409. Antes, un error distinto a 409
      // (p. ej. una violación de FK que devolvía 500) se re-lanzaba en silencio
      // y el usuario veía que "no pasaba nada" al borrar.
      if (err instanceof ApiError) {
        setError(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : t('genreTax.errDeleteGenre'));
    }
  }

  return (
    <div className="surface-card flex flex-col gap-space-sm">
      {editing ? (
        <NameForm
          title={t('genreTax.renameTitle', { name: genre.name })}
          initial={genre.name}
          placeholder={t('genreTax.newNamePlaceholder')}
          hint={t('genreTax.renameHint')}
          onSubmit={rename}
          onDone={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="flex items-center justify-between gap-space-sm">
          <button
            type="button"
            className="flex items-center gap-space-xs min-w-0 flex-1 text-left"
            onClick={() => setOpen((o) => !o)}
          >
            <Icon name={open ? 'expand_more' : 'chevron_right'} className="text-[18px] text-on-surface-variant" />
            <Icon name="category" className="text-[18px] text-primary" />
            <h3 className="font-headline-md text-headline-md text-on-surface tracking-tight truncate">
              {genre.name}
            </h3>
          </button>
          {canEdit && (
            <div className="flex items-center gap-space-xs shrink-0">
              <button
                className="w-9 h-9 rounded bg-surface-container text-primary flex items-center justify-center"
                type="button"
                aria-label={t('genreTax.rename')}
                onClick={() => setEditing(true)}
              >
                <Icon name="edit" className="text-[18px]" />
              </button>
              <button
                className="w-9 h-9 rounded bg-surface-container text-priority-urgent flex items-center justify-center"
                type="button"
                aria-label={t('genreTax.delete')}
                onClick={() => {
                  if (confirm(t('genreTax.deleteGenreConfirm', { name: genre.name }))) void remove();
                }}
              >
                <Icon name="delete" className="text-[18px]" />
              </button>
            </div>
          )}
        </div>
      )}

      <AlertMessage message={error} />

      {open && !editing && (
        <div className="pl-space-md flex flex-col gap-space-xs border-l border-border-subtle">
          <AsyncBoundary state={subs}>
            {(data) => (
              <SubgenreList
                genre={genre}
                allGenres={allGenres}
                subgenres={data.items}
                canEdit={canEdit}
                onChanged={() => {
                  subs.reload();
                  onChanged();
                }}
              />
            )}
          </AsyncBoundary>
        </div>
      )}
    </div>
  );
}

/** Lista de subgéneros de un género: alta, edición (nombre + reasignar padre), borrado. */
function SubgenreList({
  genre,
  allGenres,
  subgenres,
  canEdit,
  onChanged,
}: {
  genre: GenreOption;
  allGenres: GenreOption[];
  subgenres: Subgenre[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-space-xs py-space-xs">
      {subgenres.length === 0 && !adding && (
        <span className="font-body-sm text-body-sm text-on-surface-variant">{t('genreTax.noSubgenres')}</span>
      )}
      {subgenres.map((s) =>
        editingId === s.id ? (
          <SubgenreEditor
            key={s.id}
            subgenre={s}
            allGenres={allGenres}
            onSubmit={async (name, genreId) => {
              await api.genres.subgenreUpdate(genre.id, s.id, {
                name,
                genre_id: genreId !== genre.id ? genreId : undefined,
              });
            }}
            onDone={() => {
              setEditingId(null);
              onChanged();
            }}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <div key={s.id} className="flex items-center justify-between gap-space-xs p-space-xs rounded-lg bg-surface-base">
            <span className="font-body-md text-body-md text-on-surface truncate">{s.name}</span>
            {canEdit && (
              <div className="flex items-center gap-space-xs shrink-0">
                <button
                  className="w-8 h-8 rounded text-primary flex items-center justify-center"
                  type="button"
                  aria-label={t('genreTax.editSubgenre')}
                  onClick={() => setEditingId(s.id)}
                >
                  <Icon name="edit" className="text-[16px]" />
                </button>
                <button
                  className="w-8 h-8 rounded text-priority-urgent flex items-center justify-center"
                  type="button"
                  aria-label={t('genreTax.deleteSubgenre')}
                  onClick={() => {
                    if (confirm(t('genreTax.deleteSubgenreConfirm', { name: s.name }))) {
                      void api.genres.subgenreRemove(genre.id, s.id).then(onChanged);
                    }
                  }}
                >
                  <Icon name="delete" className="text-[16px]" />
                </button>
              </div>
            )}
          </div>
        ),
      )}

      {canEdit &&
        (adding ? (
          <NameForm
            title={t('genreTax.newSubgenre')}
            placeholder={t('genreTax.subgenreNamePlaceholder')}
            onSubmit={(name) => api.genres.subgenreCreate(genre.id, { name }).then(() => undefined)}
            onDone={() => {
              setAdding(false);
              onChanged();
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button className="btn-secondary btn-sm self-start" type="button" onClick={() => setAdding(true)}>
            <Icon name="add" className="text-[16px]" /> {t('genreTax.subgenre')}
          </button>
        ))}
    </div>
  );
}

/** Editor de un subgénero: nombre + género padre (reasignar taxonomía). */
function SubgenreEditor({
  subgenre,
  allGenres,
  onSubmit,
  onDone,
  onCancel,
}: {
  subgenre: Subgenre;
  allGenres: GenreOption[];
  onSubmit: (name: string, genreId: string) => Promise<void>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(subgenre.name);
  const [genreId, setGenreId] = useState(subgenre.genre_id);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(name.trim(), genreId);
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-space-xs p-space-sm rounded-lg bg-surface-container">
      <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} required />
      <label className="field-label">{t('genreTax.parentGenre')}</label>
      <select className="field-input" value={genreId} onChange={(e) => setGenreId(e.target.value)}>
        {allGenres.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
      <div className="flex gap-space-xs">
        <button className="btn-primary flex-1 btn-sm" type="submit" disabled={busy}>
          {busy ? t('genreTax.saving') : t('genreTax.save')}
        </button>
        <button className="btn-secondary btn-sm" type="button" onClick={onCancel} disabled={busy}>
          {t('genreTax.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Formulario simple de un nombre (crear/renombrar género o subgénero). */
function NameForm({
  title,
  initial = '',
  placeholder,
  hint,
  onSubmit,
  onDone,
  onCancel,
}: {
  title: string;
  initial?: string;
  placeholder?: string;
  hint?: string;
  onSubmit: (name: string) => Promise<void>;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(name.trim());
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('genreTax.errSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={title} icon="category">
      <form onSubmit={submit} className="flex flex-col gap-space-sm">
        <input
          className="field-input"
          value={name}
          placeholder={placeholder}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
        />
        {hint && <p className="font-body-sm text-body-sm text-on-surface-variant">{hint}</p>}
        {error && <p className="text-priority-urgent font-body-sm text-body-sm">{error}</p>}
        <div className="flex gap-space-sm">
          <button className="btn-primary flex-1" type="submit" disabled={busy}>
            {busy ? t('genreTax.saving') : t('genreTax.save')}
          </button>
          {onCancel && (
            <button className="btn-secondary" type="button" onClick={onCancel} disabled={busy}>
              {t('genreTax.cancel')}
            </button>
          )}
        </div>
      </form>
    </Card>
  );
}
